import React, { useState, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { Button, Container, Form, Card, Navbar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DoorOpen, StopCircle, PlayCircle, Incognito, RewindCircle } from "react-bootstrap-icons";
import './Poker.css'; // Import the CSS file

interface Room {
    id?: string;
    roomName: string;
    clients: Record<string, { name: string; observer: boolean }>;
    votes: Record<string, number>;
    votingActive: boolean;
}

const CARD_UNKNOWN = -1;
const CARD_EMPTY = -2;

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const Poker: React.FC = () => {
    const query = new URLSearchParams(window.location.search);
    const nameParam = query.get('name');
    const roomParam = query.get('room');

    const [socket, setSocket] = useState<Socket | null>(null);
    const [name, setName] = useState<string>(nameParam ?? '');
    const [room, setRoom] = useState<string>(roomParam ?? '');
    const [observer, setObserver] = useState<boolean>(false);
    const [scrumMaster, setScrumMaster] = useState<boolean>(false);
    const [results, setResults] = useState<Room | null>(null);
    const [nameError, setNameError] = useState<string>('');

    useEffect(() => {
        const newSocket = io(backendUrl);
        setSocket(newSocket);

        newSocket.on('roomUpdate', (room: Room) => {
            room.id = newSocket.id;
            setResults(room);
            console.log('roomUpdate', room);
        });

        return () => {
            newSocket.close();
        };
    }, []);

    const joinRoom = () => {
        // Clear previous errors
        setNameError('');

        // Validate name on client side
        const { error: validationError } = validateClientName(name);
        if (validationError) {
            setNameError(validationError);
            return;
        }

        if (room && name && socket) {
            socket.emit('joinRoom', room, name, observer);

            // update URL with name and room
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('room', room);
            newUrl.searchParams.set('name', name);
            window.history.pushState({}, '', newUrl.toString());
        }
    };

    const leaveRoom = () => {
        if (results && socket) {
            socket.emit('leaveRoom', results.roomName);
            setResults(null);
        }
    };

    const startVoting = () => {
        if (results && socket) {
            socket.emit('startVoting', results.roomName);
        }
    };

    const endVoting = () => {
        if (results && socket) {
            socket.emit('endVoting', results.roomName);
        }
    };

    const vote = (number: number) => {
        if (results && socket) {
            socket.emit('vote', results.roomName, number);
        }
    };

    const unvote = () => {
        if (results && socket) {
            socket.emit('unvote', results.roomName);
        }
    };

    if (!results) {
        return (
            <Container className="d-flex justify-content-center align-items-center vh-100">
                <Card style={{ width: "400px" }} className="border-orange">
                    <Card.Header className="text-center bg-orange border-orange text-white fw-semibold">
                        Login
                    </Card.Header>
                    <Card.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    isInvalid={!!nameError}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {nameError}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <div className="d-flex gap-3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Observer"
                                        checked={observer}
                                        onChange={(e) => {
                                            setObserver(e.target.checked);
                                            if (e.target.checked) {
                                                setScrumMaster(false);
                                            }
                                        }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label="Scrum Master"
                                        checked={scrumMaster}
                                        onChange={(e) => {
                                            setScrumMaster(e.target.checked);
                                            if (e.target.checked) {
                                                setObserver(false);
                                            }
                                        }}
                                    />
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Room</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter room name"
                                    value={room}
                                    onChange={(e) => setRoom(e.target.value)}
                                />
                            </Form.Group>

                            <Button variant="warning" className="w-50 bg-orange text-white" onClick={joinRoom}>
                                Enter
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    const hasVoted = results.votes[results.id || ''] !== undefined;

    return (
        <Container fluid className="p-0">
            {/* Top Bar */}
            <Navbar className="bg-orange text-white py-2 d-flex justify-content-between">
                {scrumMaster && !results.votingActive && (
                    <OverlayTrigger placement="right" overlay={<Tooltip>Start Voting</Tooltip>}>
                        <Button variant="link" className="text-white p-0 ms-3" onClick={startVoting}>
                            <PlayCircle size={30} />
                        </Button>
                    </OverlayTrigger>
                )}
                
                {scrumMaster && results.votingActive && (
                    <OverlayTrigger placement="right" overlay={<Tooltip>Stop Voting</Tooltip>}>
                        <Button variant="link" className="text-white p-0 ms-3" onClick={endVoting}>
                            <StopCircle size={30} />
                        </Button>
                    </OverlayTrigger>
                )}

                {!observer && results.votingActive && hasVoted && (
                    <OverlayTrigger placement="right" overlay={<Tooltip>Undo Vote</Tooltip>}>
                        <Button variant="link" className="text-white p-0 ms-3" onClick={unvote}>
                            <RewindCircle size={30} />
                        </Button>
                    </OverlayTrigger>
                )}

                <Navbar.Brand className="text-white mx-auto fw-semibold">{name} ({room}) {observer && <Incognito />}</Navbar.Brand>
            </Navbar>

            <Results results={results} vote={vote} />

            {/* Bottom Bar */}
            <Navbar className="bg-orange text-white py-2 fixed-bottom">
                <OverlayTrigger placement="right" overlay={<Tooltip>Leave Room</Tooltip>}>
                    <Button variant="link" className="text-white p-0 ms-3" onClick={leaveRoom}>
                        <DoorOpen size={30} />
                    </Button>
                </OverlayTrigger>
            </Navbar>
        </Container>
    );
};

const Results: React.FC<{ results: Room, vote: (number: number) => void }> = ({ results, vote }) => {
    const { observer } = results.clients[results.id || ''];
    const hasVoted = results.votes[results.id || ''] !== undefined;

    return (
        <>
            {results.votingActive && (!hasVoted && !observer) &&
                <div className="results-grid">
                    <button className="vote-button" onClick={() => vote(0)} ><PokerCard value={0} /></button>
                    <button className="vote-button" onClick={() => vote(1)} ><PokerCard value={1} /></button>
                    <button className="vote-button" onClick={() => vote(2)} ><PokerCard value={2} /></button>
                    <button className="vote-button" onClick={() => vote(3)} ><PokerCard value={3} /></button>
                    <button className="vote-button" onClick={() => vote(5)} ><PokerCard value={5} /></button>
                    <button className="vote-button" onClick={() => vote(8)} ><PokerCard value={8} /></button>
                    <button className="vote-button" onClick={() => vote(13)} ><PokerCard value={13} /></button>
                    <button className="vote-button" onClick={() => vote(21)} ><PokerCard value={21} /></button>
                    <button className="vote-button" onClick={() => vote(40)} ><PokerCard value={40} /></button>
                    <button className="vote-button" onClick={() => vote(100)} ><PokerCard value={100} /></button>
                    <button className="vote-button" onClick={() => vote(CARD_UNKNOWN)} ><PokerCard value={CARD_UNKNOWN} /></button>
                </div>
            }
            <div className="results-grid">
                {results.votingActive && (hasVoted || observer) &&
                    Object.keys(results.clients).map(clientId => {
                        const { name, observer } = results.clients[clientId];
                        const voteStatus = results.votes[clientId] !== undefined? undefined : CARD_EMPTY;
                        return !observer && <PokerCard key={clientId} value={voteStatus} name={name} />
                    })
                }

                {!results.votingActive &&
                    Object.keys(results.clients).map(clientId => {
                        const { name, observer } = results.clients[clientId];
                        return !observer && <PokerCard key={clientId} value={results.votes[clientId] ?? CARD_EMPTY} name={name} />
                    })
                }
            </div>
        </>
    );
}

const PokerCard: React.FC<{ value?: number, name?: string }> = ({ value, name }) => {
    const fileName = value === undefined ? "/PokerCards_back_N.jpg" : `/PokerCards_front_${value}_N.jpg`;

    return (
        <div className="card-container">
            {name && <p className="card-name">{name}</p>}
            <img className="card-image" src={fileName} alt={`Card ${value}`} />
        </div>
    );
}

// Validation function for client names
const validateClientName = (name: string): { error?: string } => {
    if (!name) {
        return { error: 'Name is required' };
    }

    if (name.length > 12) {
        return { error: 'Name must not exceed 12 characters' };
    }

    // Only allow letters, numbers, spaces, hyphens, underscores, and dots
    const validNameRegex = /^[a-zA-Z0-9\s\-_.]+$/;
    if (!validNameRegex.test(name)) {
        return { error: 'Name can only contain letters, numbers, spaces, hyphens, underscores, and dots' };
    }

    return { error: undefined };
};



export default Poker;